import { Controller } from "@hotwired/stimulus"
import { initializePaddle } from "../lib/paddle"

export default class PricingController extends Controller {
  static targets = ["tier", "monthlyPrice", "yearlyPrice", "monthlyPeriod", "yearlyPeriod", "toggleButton"]
  static values = {
    clientToken: String,
    environment: String,
    userEmail: String,
    successUrl: String
  }

  declare clientTokenValue: string
  declare environmentValue: string
  declare hasUserEmailValue: boolean
  declare userEmailValue: string
  declare successUrlValue: string

  declare readonly tierTargets: HTMLElement[]
  declare readonly monthlyPriceTargets: HTMLElement[]
  declare readonly yearlyPriceTargets: HTMLElement[]
  declare readonly monthlyPeriodTargets: HTMLElement[]
  declare readonly yearlyPeriodTargets: HTMLElement[]
  declare readonly toggleButtonTargets: HTMLElement[]

  private paddleReady = false
  private frequency: "month" | "year" = "month"

  connect() {
    initializePaddle({
      token: this.clientTokenValue,
      environment: this.environmentValue,
      checkoutSettings: {
        displayMode: "overlay",
        variant: "one-page",
        successUrl: this.successUrlValue
      }
    })
      .then(() => {
        this.paddleReady = true
        this.fetchPrices()
      })
      .catch((error) => {
        console.error("Paddle initialization failed", error)
      })
  }

  setFrequency({ params }: { params: { frequency: string } }) {
    this.frequency = params.frequency === "year" ? "year" : "month"
    this.monthlyPriceTargets.forEach((el) => {
      el.hidden = this.frequency === "year"
    })
    this.yearlyPriceTargets.forEach((el) => {
      el.hidden = this.frequency === "month"
    })
    this.monthlyPeriodTargets.forEach((el) => {
      el.hidden = this.frequency === "year"
    })
    this.yearlyPeriodTargets.forEach((el) => {
      el.hidden = this.frequency === "month"
    })
    this.toggleButtonTargets.forEach((btn) => {
      const on = btn.dataset.pricingFrequencyParam === params.frequency
      btn.setAttribute("aria-pressed", String(on))
      const activeClasses = (btn.dataset.pricingActiveClassValue || "").split(" ").filter(Boolean)
      const inactiveClasses = (btn.dataset.pricingInactiveClassValue || "").split(" ").filter(Boolean)
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      btn.classList.add(...(on ? activeClasses : inactiveClasses))
    })
  }

  openCheckout(event: Event) {
    if (!this.paddleReady) return
    const tier = (event.currentTarget as HTMLElement).closest("[data-pricing-target='tier']") as HTMLElement
    const priceId = tier.dataset[this.frequency === "year" ? "priceYear" : "priceMonth"]!
    Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      ...(this.hasUserEmailValue && { customer: { email: this.userEmailValue } })
    })
  }

  private fetchPrices(attempt = 0): void {
    const items = this.tierTargets.flatMap((tier) => [
      { priceId: tier.dataset.priceMonth!, quantity: 1 },
      { priceId: tier.dataset.priceYear!, quantity: 1 }
    ])
    Paddle.PricePreview({ items })
      .then((response) => {
        const totals = new Map<string, string>()
        response.data.details.lineItems.forEach((item) => {
          totals.set(item.price.id, item.formattedTotals.total)
        })
        this.applyTotals(totals)
      })
      .catch(() => {
        if (attempt < 3) {
          setTimeout(() => this.fetchPrices(attempt + 1), 400)
        }
      })
  }

  private applyTotals(totals: Map<string, string>): void {
    this.monthlyPriceTargets.forEach((el) => {
      const priceId = (el.closest("[data-pricing-target='tier']") as HTMLElement | null)?.dataset.priceMonth
      if (priceId) el.textContent = totals.get(priceId) ?? el.textContent
    })
    this.yearlyPriceTargets.forEach((el) => {
      const priceId = (el.closest("[data-pricing-target='tier']") as HTMLElement | null)?.dataset.priceYear
      if (priceId) el.textContent = totals.get(priceId) ?? el.textContent
    })
  }
}
